// Utilities for safely working with React Query InfiniteQuery results
// Provides defensive flattening and cursor extraction that tolerate undefined structures.

export type InfinitePage<T = any> = {
  items?: T[];
  cursor?: string | null;
  // Allow unknown extra fields
  [key: string]: any;
};

// Support both React Query Infinite data shape and single-page API shape
export type PagedResponse<T = any> = {
  items?: T[];
  metadata?: { cursor?: string | null; [key: string]: any } | any;
  cursor?: string | null;
  [key: string]: any;
};

export type InfiniteData<T = any> = {
  pages?: InfinitePage<T>[];
  pageParams?: unknown[];
} & Partial<PagedResponse<T>>;

/**
 * Safely flatten `items` from an InfiniteQuery result OR a single-page `{ items }` response.
 * Returns an empty array if no items are available yet.
 */
export function flattenInfiniteItems<T = any>(data?: InfiniteData<T> | null): T[] {
  if (!data) return [];

  // Single-page response `{ items: [...] }`
  const maybeItems = (data as PagedResponse<T>)?.items;
  if (Array.isArray(maybeItems)) return maybeItems as T[];

  // React Query Infinite shape `{ pages: [...] }`
  const pages = (data as { pages?: InfinitePage<T>[] })?.pages ?? [];
  if (!Array.isArray(pages) || pages.length === 0) return [];

  // Use flatMap defensively with per-page fallback
  return pages.flatMap((p) => (Array.isArray(p?.items) ? (p.items as T[]) : []));
}

/**
 * Get the last cursor from Infinite pages or fall back to single-page `cursor`/`metadata.cursor`.
 */
export function getLastCursor<T = any>(data?: InfiniteData<T> | null): string | null {
  if (!data) return null;

  // Prefer Infinite pages' last cursor
  const pages = (data as { pages?: InfinitePage<T>[] })?.pages ?? [];
  if (Array.isArray(pages) && pages.length > 0) {
    const last = pages[pages.length - 1];
    if (last?.cursor) return last.cursor;
  }

  // Fall back to single-page cursor fields
  const paged = data as PagedResponse<T>;
  if (paged?.cursor) return paged.cursor;
  if (paged?.metadata?.cursor) return paged.metadata.cursor;

  return null;
}
